import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import {
    PersonPlusFill, PatchCheckFill, Wallet2, TrophyFill, ArrowRight,
    BroadcastPin, LightningChargeFill, ShieldLockFill, GraphUpArrow, CheckCircleFill, CarFrontFill,
} from "react-bootstrap-icons";
import { formatDateTime } from "../../common/format";
import { t } from "./auctionI18n";
import LiveAuctionDemo from "./LiveAuctionDemo";
import "./AuctionLanding.css";
import "../../common/ui/ui.css";

const HERO_IMAGE = "/static/main-page-banner.jpg";

// Google Tag Manager container for the auction landing campaign. This is a
// separate container from the site-wide one in index.html (GTM-KB4GGHCL) and
// is loaded only while this landing page is mounted.
const LANDING_GTM_ID = "GTM-KB6X6693";

const AuctionLanding = () => {
    const [stats, setStats] = useState(null);
    const [featured, setFeatured] = useState({ event: null, lots: [] });
    // An already-authenticated user has no reason to see "Register" — mirror the
    // header, which swaps the sign-in/register buttons for the profile menu.
    const loggedIn = useSelector((s) => s.user.isLoggedIn);

    const STEPS = [
        { icon: <PersonPlusFill />, title: t("დარეგისტრირდი", "Register", "Регистрация", "Գրանցվեք"), text: t("შექმენი ანგარიში რამდენიმე წუთში და მიიღე წვდომა აუქციონზე.", "Create an account in minutes and get access to the auction.", "Создайте аккаунт за пару минут и получите доступ к аукциону.", "Ստեղծեք հաշիվ րոպեների ընթացքում և ստացեք մուտք դեպի աճուրդ։") },
        { icon: <PatchCheckFill />, title: t("გაიარე ვერიფიკაცია", "Get verified", "Пройдите верификацию", "Անցեք վերիֆիկացիա"), text: t("ატვირთე პირადობა (KYC) უსაფრთხო ბიდისთვის.", "Upload your ID (KYC) for secure bidding.", "Загрузите удостоверение (KYC) для безопасных ставок.", "Վերբեռնեք անձնագիրը (KYC) ապահով հայտերի համար։") },
        { icon: <Wallet2 />, title: t("შეიტანე დეპოზიტი", "Make a deposit", "Внесите депозит", "Կատարեք ավանդ"), text: t("ბიდის დასადებად საჭიროა თანხის 10% თავისუფალ ბალანსზე.", "To bid you need 10% of the amount as free balance.", "Для ставки нужно 10% суммы на свободном балансе.", "Հայտ դնելու համար անհրաժեշտ է գումարի 10%-ը ազատ հաշվեկշռին։") },
        { icon: <TrophyFill />, title: t("დადე ბიდი და მოიგე", "Bid and win", "Делайте ставки и побеждайте", "Դրեք հայտ և հաղթեք"), text: t("მონაწილეობა მიიღე ლაივ აუქციონზე და მოიგე სასურველი ავტომობილი.", "Join the live auction and win the car you want.", "Участвуйте в живом аукционе и выиграйте желаемый автомобиль.", "Մասնակցեք կենդանի աճուրդին և շահեք ցանկալի մեքենան։") },
    ];

    const FEATURES = [
        { icon: <BroadcastPin />, title: t("ნამდვილი ლაივ აუქციონი", "A real live auction", "Настоящий живой аукцион", "Իսկական կենդանի աճուրդ"), text: t("ავტომობილები რიგდება და იყიდება რეალურ დროში, Copart-ის სტილში — ყოველ ბიდზე ტაიმერი ახლდება.", "Cars are queued and sold in real time, Copart-style — every bid resets the timer.", "Автомобили выставляются и продаются в реальном времени, в стиле Copart — каждая ставка сбрасывает таймер.", "Մեքենաները հերթագրվում և վաճառվում են իրական ժամանակում, Copart-ի ոճով — յուրաքանչյուր հայտ զրոյացնում է ժամաչափը։") },
        { icon: <LightningChargeFill />, title: t("ავტომატური ბიდი (proxy)", "Automatic bidding (proxy)", "Автоматическая ставка (proxy)", "Ավտոմատ հայտ (proxy)"), text: t("დააყენე მაქსიმალური ფასი და სისტემა ავტომატურად დაგიცავს პოზიციას შენს ნაცვლად.", "Set a maximum price and the system defends your position automatically.", "Задайте максимальную цену, и система автоматически защитит вашу позицию.", "Սահմանեք առավելագույն գինը, և համակարգը ավտոմատ կպաշտպանի ձեր դիրքը։") },
        { icon: <ShieldLockFill />, title: t("უსაფრთხო და გამჭვირვალე", "Secure and transparent", "Безопасно и прозрачно", "Ապահով և թափանցիկ"), text: t("KYC ვერიფიკაცია, დაცული დეპოზიტები და ყველა ბიდის სრული ისტორია შენს ანგარიშზე.", "KYC verification, protected deposits and a full history of every bid in your account.", "KYC-верификация, защищённые депозиты и полная история всех ставок в вашем аккаунте.", "KYC վերիֆիկացիա, պաշտպանված ավանդներ և բոլոր հայտերի ամբողջական պատմությունը ձեր հաշվում։") },
        { icon: <GraphUpArrow />, title: t("სრული კონტროლი", "Full control", "Полный контроль", "Լիակատար վերահսկողություն"), text: t("თვალი ადევნე ბიდებს, რჩეულებს და მოგებულ ავტომობილებს — ინვოისი ხელმისაწვდომია მაშინვე.", "Track your bids, watchlist and won cars — the invoice is available instantly.", "Следите за ставками, избранным и выигранными авто — счёт доступен сразу.", "Հետևեք ձեր հայտերին, ցանկին և շահած մեքենաներին — հաշիվը հասանելի է անմիջապես։") },
    ];

    // Load the Google Tag Manager campaign container once, only on this page.
    useEffect(() => {
        if (document.getElementById("gtm-auction-landing")) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
        const script = document.createElement("script");
        script.id = "gtm-auction-landing";
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtm.js?id=${LANDING_GTM_ID}`;
        document.head.appendChild(script);

        const noscript = document.createElement("noscript");
        noscript.id = "gtm-auction-landing-ns";
        const iframe = document.createElement("iframe");
        iframe.src = `https://www.googletagmanager.com/ns.html?id=${LANDING_GTM_ID}`;
        iframe.height = "0";
        iframe.width = "0";
        iframe.style.display = "none";
        iframe.style.visibility = "hidden";
        noscript.appendChild(iframe);
        document.body.appendChild(noscript);
    }, []);

    useEffect(() => {
        let alive = true;
        axios.get("/api/auction/events")
            .then((res) => {
                if (!alive) return;
                const events = res.data.events || [];
                const liveEvents = events.filter((e) => e.status === "live" || e.status === "paused");
                const lots = events.reduce((sum, e) => sum + (Number(e.lot_count) || 0), 0);
                const scheduled = events
                    .filter((e) => e.status === "scheduled")
                    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
                const next = scheduled[0];
                setStats({ events: events.length, live: liveEvents.length, lots, next });

                // Pick an event to showcase cars from: a live one first, then the
                // soonest upcoming, then the most recent — and pull its lots.
                const showcase = liveEvents[0] || next || events[0];
                if (!showcase) return;
                axios.get(`/api/auction/events/${showcase.id}`)
                    .then((r) => {
                        if (!alive) return;
                        const all = r.data.lots || [];
                        // Hide withdrawn lots; prefer live/open ones first.
                        const rank = { live: 0, open: 1, pending: 2, sold: 3, closed: 4 };
                        const visible = all
                            .filter((l) => l.status !== "withdrawn")
                            .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9));
                        setFeatured({ event: r.data.event || showcase, lots: visible.slice(0, 8) });
                    })
                    .catch(() => {});
            })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    return (
        <div className="auction-landing">
            {/* Hero */}
            <section className="al-hero">
                <video className="al-hero-video" autoPlay muted loop playsInline preload="auto" poster={HERO_IMAGE}>
                    <source src="/static/auction-hero.mp4" type="video/mp4" />
                </video>
                <div className="al-hero-overlay" />
                <div className="container al-hero-inner">
                    <span className="al-badge"><BroadcastPin /> {t("ონლაინ ლაივ აუქციონი", "Online live auction", "Онлайн живой аукцион", "Առցանց կենդանի աճուրդ")}</span>
                    <h1 className="al-hero-title">{t("ავტომობილის აუქციონი", "Car auction", "Автоаукцион", "Մեքենաների աճուրդ")} <span>{t("ონლაინ", "online", "онлайн", "առցանց")}</span></h1>
                    <p className="al-hero-sub">
                        {t(
                            "დარეგისტრირდი, გაიარე ვერიფიკაცია და დადე ბიდი ლაივ აუქციონზე — გამჭვირვალედ, უსაფრთხოდ და რეალურ დროში. სასურველი ავტომობილი ახლა ერთ კლიკზეა.",
                            "Register, get verified and bid in the live auction — transparently, securely and in real time. The car you want is now one click away.",
                            "Зарегистрируйтесь, пройдите верификацию и делайте ставки на живом аукционе — прозрачно, безопасно и в реальном времени. Желаемый автомобиль теперь в один клик.",
                            "Գրանցվեք, անցեք վերիֆիկացիա և դրեք հայտ կենդանի աճուրդում — թափանցիկ, ապահով և իրական ժամանակում։ Ցանկալի մեքենան այժմ մեկ կտտոցի հեռավորության վրա է։"
                        )}
                    </p>
                    <div className="al-hero-cta">
                        {!loggedIn && (
                        <Link to="/auction/register" className="cx-btn cx-btn-primary al-btn-lg">
                            <PersonPlusFill /> {t("რეგისტრაცია", "Register", "Регистрация", "Գրանցում")}
                        </Link>
                        )}
                        <Link to="/auctions" className={`cx-btn al-btn-lg ${loggedIn ? "cx-btn-primary" : "cx-btn-ghost"}`}>
                            {t("მიმდინარე აუქციონები", "Current auctions", "Текущие аукционы", "Ընթացիկ աճուրդներ")} <ArrowRight />
                        </Link>
                    </div>

                    {stats && (
                        <div className="al-hero-stats">
                            <div className="al-stat">
                                <strong>{stats.live > 0 ? <span className="al-live-dot-wrap"><span className="al-live-dot" />{stats.live}</span> : stats.events}</strong>
                                <span>{stats.live > 0 ? t("ლაივ აუქციონი", "Live auctions", "Живые аукционы", "Կենդանի աճուրդներ") : t("აქტიური აუქციონი", "Active auctions", "Активные аукционы", "Ակտիվ աճուրդներ")}</span>
                            </div>
                            <div className="al-stat"><strong>{stats.lots}</strong><span>{t("ავტომობილი ლოტებში", "Cars in lots", "Авто в лотах", "Մեքենաներ լոտերում")}</span></div>
                            {stats.next && (
                                <div className="al-stat al-stat-next">
                                    <strong>{formatDateTime(stats.next.starts_at)}</strong>
                                    <span>{t("შემდეგი აუქციონი", "Next auction", "Следующий аукцион", "Հաջորդ աճուրդ")}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Self-playing live auction simulation */}
            {featured.lots.length > 0 && (
                <LiveAuctionDemo cars={featured.lots} event={featured.event} loggedIn={loggedIn} />
            )}

            {/* How it works */}
            <section className="container al-section">
                <div className="al-section-head">
                    <span className="al-eyebrow">{t("მარტივად", "Simple", "Просто", "Պարզ")}</span>
                    <h2>{t("როგორ მუშაობს", "How it works", "Как это работает", "Ինչպես է աշխատում")}</h2>
                    <p>{t("ოთხი ნაბიჯი აუქციონზე პირველ ბიდამდე.", "Four steps to your first bid.", "Четыре шага до первой ставки.", "Չորս քայլ մինչև ձեր առաջին հայտը։")}</p>
                </div>
                <div className="al-steps">
                    {STEPS.map((s, i) => (
                        <div key={i} className="al-step">
                            <span className="al-step-num">{i + 1}</span>
                            <span className="al-step-icon">{s.icon}</span>
                            <h3>{s.title}</h3>
                            <p>{s.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features */}
            <section className="container al-section">
                <div className="al-section-head">
                    <span className="al-eyebrow">{t("უპირატესობები", "Advantages", "Преимущества", "Առավելություններ")}</span>
                    <h2>{t("რატომ ჩვენი აუქციონი", "Why our auction", "Почему наш аукцион", "Ինչու մեր աճուրդը")}</h2>
                </div>
                <div className="al-features">
                    {FEATURES.map((f, i) => (
                        <div key={i} className="al-feature cx-card">
                            <span className="al-feature-icon">{f.icon}</span>
                            <h3>{f.title}</h3>
                            <p>{f.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Final CTA */}
            <section className="container al-section">
                <div className="al-cta-banner">
                    <CarFrontFill className="al-cta-bg-icon" />
                    <div className="al-cta-content">
                        <h2>{t("მზად ხარ პირველი ბიდისთვის?", "Ready for your first bid?", "Готовы к первой ставке?", "Պատրա՞ստ եք ձեր առաջին հայտին")}</h2>
                        <p>{t("შექმენი ანგარიში დღესვე და შეუერთდი ლაივ აუქციონს.", "Create an account today and join the live auction.", "Создайте аккаунт сегодня и присоединяйтесь к живому аукциону.", "Ստեղծեք հաշիվ այսօր և միացեք կենդանի աճուրդին։")}</p>
                        <ul className="al-cta-points">
                            <li><CheckCircleFill /> {t("უფასო რეგისტრაცია", "Free registration", "Бесплатная регистрация", "Անվճար գրանցում")}</li>
                            <li><CheckCircleFill /> {t("რამდენიმე წუთში", "In a few minutes", "За несколько минут", "Մի քանի րոպեում")}</li>
                            <li><CheckCircleFill /> {t("უსაფრთხო გადახდები", "Secure payments", "Безопасные платежи", "Ապահով վճարումներ")}</li>
                        </ul>
                        {loggedIn ? (
                            <Link to="/auctions" className="cx-btn cx-btn-primary al-btn-lg">
                                {t("მიმდინარე აუქციონები", "Current auctions", "Текущие аукционы", "Ընթացիկ աճուրդներ")} <ArrowRight />
                            </Link>
                        ) : (
                            <Link to="/auction/register" className="cx-btn cx-btn-primary al-btn-lg">
                                <PersonPlusFill /> {t("დაიწყე რეგისტრაცია", "Start registration", "Начать регистрацию", "Սկսել գրանցումը")}
                            </Link>
                        )}
                        {!loggedIn && (
                        <p className="al-cta-note">{t("უკვე გაქვს ანგარიში?", "Already have an account?", "Уже есть аккаунт?", "Արդեն ունե՞ք հաշիվ")} <Link to="/auction/login">{t("შესვლა", "Sign in", "Войти", "Մուտք")}</Link></p>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AuctionLanding;
